import { Controller } from '@hotwired/stimulus'
import consumer from '../channels/consumer'

const PREP_DONE_AT = {
  placed: ['placed', 'confirmed', 'ready', 'out_for_delivery', 'delivered'],
  confirmed: ['confirmed', 'ready', 'out_for_delivery', 'delivered'],
  ready: ['ready', 'out_for_delivery', 'delivered']
}

const DELIVERY_DONE_AT = {
  packed: ['ready', 'out_for_delivery', 'delivered'],
  out_for_delivery: ['out_for_delivery', 'delivered'],
  fulfilled: ['delivered']
}

const DISPATCH_LABELS = {
  placed: 'Shop preparing',
  confirmed: 'Shop preparing',
  ready: 'Waiting for shop dispatch',
  out_for_delivery: 'On the way to you',
  delivered: 'Delivered',
  canceled: 'Delivery canceled'
}

export default class extends Controller {
  static targets = [
    'track',
    'scooter',
    'midDot',
    'endDot',
    'prepBadge',
    'prepStep',
    'dispatchBadge',
    'dispatchLabel',
    'deliveryBadge',
    'deliveryStep',
    'pinPanel'
  ]

  static values = {
    orderId: Number,
    state: String,
    percent: { type: Number, default: 0 }
  }

  connect () {
    this.applyProgress(this.percentValue, this.stateValue)
    if (!this.orderIdValue) return

    this.subscription = consumer.subscriptions.create(
      { channel: 'OrderTrackingChannel', order_id: this.orderIdValue },
      { received: (data) => this.receive(data) }
    )
  }

  disconnect () {
    if (this.subscription) this.subscription.unsubscribe()
  }

  receive (data) {
    if (data?.type !== 'shop_delivery_status') return

    const state = String(data.state || '')
    this.applyProgress(Number(data.progress_percent), state)
    this.updatePrepSteps(state)
    this.updateDispatchUi(state)
    this.updateDeliverySteps(state)
    this.updatePinPanel(Boolean(data.pin_available))
  }

  applyProgress (percent, state) {
    const value = Math.max(0, Math.min(100, Number(percent) || 0))
    const moving = state === 'out_for_delivery'
    const canceled = state === 'canceled'

    if (this.hasTrackTarget) {
      this.trackTarget.style.width = `${Math.max(value, value > 0 ? 8 : 0)}%`
      this.trackTarget.classList.toggle('is-live', moving)
      this.trackTarget.classList.toggle('bg-gradient-to-r', !moving)
      this.trackTarget.classList.toggle('from-emerald-400', !moving)
      this.trackTarget.classList.toggle('to-green-500', !moving)
    }

    if (this.hasScooterTarget) {
      this.scooterTarget.classList.toggle('hidden', value <= 0 || canceled)
      this.scooterTarget.style.setProperty('--shop-scooter-left', `${value}%`)
      this.scooterTarget.classList.toggle('is-moving', moving)
    }

    if (this.hasMidDotTarget) {
      this.midDotTarget.classList.toggle('bg-emerald-500', value >= 48)
      this.midDotTarget.classList.toggle('bg-slate-300', value < 48)
    }

    if (this.hasEndDotTarget) {
      this.endDotTarget.classList.toggle('bg-emerald-500', value >= 100)
      this.endDotTarget.classList.toggle('bg-slate-300', value < 100)
    }
  }

  updatePrepSteps (state) {
    this.updateStepTargets(this.hasPrepStepTarget ? this.prepStepTargets : [], PREP_DONE_AT, state)

    if (this.hasPrepBadgeTarget) {
      const done = ['ready', 'out_for_delivery', 'delivered'].includes(state)
      this.prepBadgeTarget.textContent = done ? 'Done' : 'In progress'
      this.prepBadgeTarget.className = `text-[10px] font-bold uppercase tracking-wide ${done ? 'text-emerald-600' : 'text-slate-400'}`
    }
  }

  updateDispatchUi (state) {
    if (this.hasDispatchLabelTarget) {
      this.dispatchLabelTarget.textContent = DISPATCH_LABELS[state] || 'Shop preparing'
    }

    if (!this.hasDispatchBadgeTarget) return

    if (state === 'delivered' || state === 'out_for_delivery' || state === 'ready') {
      const labels = {
        ready: 'Ready',
        out_for_delivery: 'On the way',
        delivered: 'Delivered'
      }
      this.dispatchBadgeTarget.textContent = labels[state] || 'Ready'
      this.dispatchBadgeTarget.className = 'rounded-md bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-800'
    } else if (state === 'canceled') {
      this.dispatchBadgeTarget.textContent = 'Canceled'
      this.dispatchBadgeTarget.className = 'rounded-md bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-rose-800'
    } else {
      this.dispatchBadgeTarget.textContent = 'Waiting'
      this.dispatchBadgeTarget.className = 'text-[10px] font-bold uppercase tracking-wide text-slate-400'
    }
  }

  updateDeliverySteps (state) {
    this.updateStepTargets(this.hasDeliveryStepTarget ? this.deliveryStepTargets : [], DELIVERY_DONE_AT, state, {
      activeKey: 'packed',
      activeStates: ['ready']
    })

    if (this.hasDeliveryBadgeTarget) {
      if (state === 'delivered') {
        this.deliveryBadgeTarget.textContent = 'Delivered'
        this.deliveryBadgeTarget.className = 'text-[10px] font-bold uppercase tracking-wide text-emerald-600'
      } else {
        this.deliveryBadgeTarget.textContent = 'Delivery'
        this.deliveryBadgeTarget.className = 'text-[10px] font-bold uppercase tracking-wide text-slate-400'
      }
    }
  }

  updateStepTargets (targets, doneMap, state, options = {}) {
    targets.forEach((el) => {
      const key = el.dataset.stepKey
      const doneStatuses = doneMap[key] || []
      const done = doneStatuses.includes(state)
      const dot = el.querySelector('[data-step-dot]')
      const meta = el.querySelector('[data-step-meta]')
      const connector = el.querySelector('[data-step-connector]')

      if (dot) {
        if (done) {
          dot.className = 'relative z-10 mx-auto flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-extrabold ring-2 bg-emerald-500 text-white ring-emerald-200'
          dot.innerHTML = '<svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/></svg>'
        } else {
          const index = el.dataset.stepIndex || '1'
          const active = options.activeKey === key && (options.activeStates || []).includes(state)
          dot.className = active
            ? 'relative z-10 mx-auto flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-extrabold ring-2 bg-emerald-100 text-emerald-700 ring-emerald-200'
            : 'relative z-10 mx-auto flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-extrabold ring-2 bg-white text-slate-400 ring-slate-200'
          dot.textContent = index
        }
      }

      if (meta && done && !meta.dataset.locked) {
        meta.textContent = 'Just now'
        meta.className = 'mt-0.5 text-[9px] tabular-nums text-slate-500'
      }

      if (connector) {
        const nextKey = connector.dataset.nextKey
        const nextDone = (doneMap[nextKey] || []).includes(state)
        connector.classList.toggle('bg-emerald-300', done && nextDone)
        connector.classList.toggle('bg-slate-200', !(done && nextDone))
      }
    })
  }

  updatePinPanel (available) {
    if (!this.hasPinPanelTarget) return

    this.pinPanelTarget.classList.toggle('hidden', !available)
    if (!available) {
      const digits = this.pinPanelTarget.querySelector('[data-pin-digits]')
      if (digits) digits.textContent = ''
    }
  }
}
